import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Flex,
  Text,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Badge,
  HStack,
  IconButton,
  Select,
  SimpleGrid,
  Card,
  CardBody,
} from '@chakra-ui/react';
import { User, FileText, ShoppingCart, File, CaretLeft, CaretRight } from 'phosphor-react';
import { useQuery } from '@tanstack/react-query';
import { useDebounceCallback } from 'usehooks-ts';
import moment from 'moment';
import 'moment/locale/pt-br';
import { ISaidaImport, INotasManualPosting, useCompanies } from '../../hooks/useCompanies';
import { SearchModeBar, SearchModeOption } from '../SearchModeBar';
import { IData } from '../../types/shared';
import { formatCNPJ } from '../../utils/FormatFunctions';
import { MonthNavigator } from '../shared/MonthNavigator';

moment.locale('pt-br');

const SEARCH_MODES: SearchModeOption[] = [
  { key: 'numero', buttonLabel: 'Número', menuLabel: 'Número', placeholder: 'Buscar por número…' },
  {
    key: 'razao_social',
    buttonLabel: 'Razão',
    menuLabel: 'Razão social',
    placeholder: 'Buscar por razão social…',
  },
  { key: 'id', buttonLabel: 'ID', menuLabel: 'ID Empresa', placeholder: 'Buscar por ID…' },
  { key: 'cnpj', buttonLabel: 'CNPJ', menuLabel: 'CNPJ', placeholder: 'Buscar por CNPJ…' },
];

interface ManualPostingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: ISaidaImport | null;
  initialDateStart?: string;
}

// Normaliza o tipo da nota para comparacao
// API retorna: 'NFe', 'NFC-e', 'NFS-e'
function getTipoNota(especie: string): 'NFE' | 'NFCE' | 'NFSE' {
  if (!especie) return 'NFSE';

  // Valores exatos da API
  if (especie === 'NFe') return 'NFE';
  if (especie === 'NFC-e') return 'NFCE';
  if (especie === 'NFS-e') return 'NFSE';

  // Fallback para outros formatos
  const normalized = especie.toUpperCase().replace(/[-\s]/g, '');
  if (normalized.includes('NFCE')) return 'NFCE';
  if (normalized.includes('NFSE')) return 'NFSE';
  if (normalized.includes('NFE')) return 'NFE';

  return 'NFSE';
}

function getBadgeColorScheme(especie: string): string {
  const tipo = getTipoNota(especie);
  if (tipo === 'NFE') return 'green';
  if (tipo === 'NFCE') return 'orange';
  return 'purple';
}

export function ManualPostingsModal({
  isOpen,
  onClose,
  item,
  initialDateStart,
}: ManualPostingsModalProps) {
  const { getNotasManualPosting, getSaidasImportsTotais } = useCompanies();

  // Estado interno para filtros do modal
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchMode, setSearchMode] = useState<'numero' | 'razao_social' | 'id' | 'cnpj'>('numero');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);

  // Filtro por tipo de nota (clique nos cards)
  const [tipoFilter, setTipoFilter] = useState<'NFE' | 'NFCE' | 'NFSE' | null>(null);

  // Estado para navegacao de mes (usando moment)
  const [currentDate, setCurrentDate] = useState(() => {
    if (initialDateStart) {
      return moment(initialDateStart);
    }
    return moment();
  });

  // Calcula as datas diretamente do currentDate
  const dateStart = currentDate.clone().startOf('month').format('YYYY-MM-DD');
  const dateEnd = currentDate.clone().endOf('month').format('YYYY-MM-DD');

  // Reset ao abrir modal
  useEffect(() => {
    if (isOpen && item) {
      setSearch('');
      setSearchInput('');
      setSearchMode('numero');
      setPage(1);
      setTipoFilter(null);
      if (initialDateStart) {
        setCurrentDate(moment(initialDateStart));
      }
    }
  }, [isOpen, item, initialDateStart]);

  // Handler para filtrar por tipo ao clicar no card
  const handleTipoFilter = (tipo: 'NFE' | 'NFCE' | 'NFSE' | null) => {
    if (tipo === tipoFilter) {
      setTipoFilter(null); // Remove filtro se clicar no mesmo
    } else {
      setTipoFilter(tipo);
    }
    setPage(1);
  };

  const handlePrevMonth = () => {
    setCurrentDate(prev => prev.clone().subtract(1, 'month'));
    setPage(1);
    setTipoFilter(null);
  };

  const handleNextMonth = () => {
    setCurrentDate(prev => prev.clone().add(1, 'month'));
    setPage(1);
    setTipoFilter(null);
  };

  const handleSelectMonth = (year: number, month: number) => {
    setCurrentDate(moment().year(year).month(month));
    setPage(1);
    setTipoFilter(null);
  };

  // Debounce para search
  const debouncedSearch = useDebounceCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, 300);

  // Query filters - busca dados com tamanho grande para permitir filtro local por tipo
  // A API nao suporta filtro por especie, entao buscamos tudo e filtramos localmente
  const queryFilters: Record<string, string> = {
    page: '1',
    size: '100', // Maximo permitido pela API (fastapi_pagination default)
    codi_usu: item?.name || '',
    date_start: dateStart,
    date_end: dateEnd,
  };
  // So adiciona search se nao estiver vazio
  if (search) {
    queryFilters.search = search;
    queryFilters.search_mode = searchMode;
  }

  // Query filters para os totais
  const totaisFilters = {
    codi_usu: item?.name || '',
    date_start: dateStart,
    date_end: dateEnd,
  };

  const { data: notasData, isFetching } = useQuery<IData<INotasManualPosting[]>>({
    queryKey: ['NotasManualPostingModal', queryFilters],
    queryFn: getNotasManualPosting,
    enabled: isOpen && !!item,
  });

  // Query para totais do usuario no mes selecionado
  const { data: totaisData, isFetching: isFetchingTotais } = useQuery<any>({
    queryKey: ['SaidasImportsTotaisModal', totaisFilters],
    queryFn: getSaidasImportsTotais,
    enabled: isOpen && !!item,
  });

  // Dados brutos da API
  const notasRaw = notasData?.data?.items || [];

  // Filtrar por tipo se houver filtro ativo (FILTRO LOCAL)
  const notasFiltradas = useMemo(() => {
    if (!tipoFilter) return notasRaw;
    return notasRaw.filter(nota => getTipoNota(nota.especie) === tipoFilter);
  }, [notasRaw, tipoFilter]);

  // Paginacao LOCAL
  const totalRegistros = notasFiltradas.length;
  const totalPages = Math.ceil(totalRegistros / size) || 1;
  const startIndex = (page - 1) * size;
  const endIndex = startIndex + size;
  const notasPaginadas = notasFiltradas.slice(startIndex, endIndex);

  // Totais - vem da API filtrado por usuario e mes do modal
  const totalNfe = totaisData?.quantity_nfe ?? 0;
  const totalNfce = totaisData?.quantity_nfce ?? 0;
  const totalNfse = totaisData?.quantity_nfse ?? 0;
  const totalGeral = totalNfe + totalNfce + totalNfse;
  const showLoadingTotais = isFetchingTotais;

  if (!item) return null;

  // Paginacao handlers
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleSizeChange = (newSize: number) => {
    setSize(newSize);
    setPage(1);
  };

  // Render loading state
  const renderLoading = () => (
    <Flex justify="center" align="center" py={8}>
      <Spinner size="lg" color="blue.500" />
      <Text ml={3} color="gray.500">
        Carregando notas...
      </Text>
    </Flex>
  );

  // Render empty state
  const renderEmpty = () => (
    <Flex justify="center" align="center" py={8}>
      <Text color="gray.500">
        {tipoFilter ? `Nenhuma nota ${tipoFilter} encontrada` : 'Nenhuma nota encontrada'}
      </Text>
    </Flex>
  );

  // Render table content
  const renderTable = () => (
    <Box>
      <TableContainer
        bg="white"
        borderRadius="lg"
        shadow="sm"
        border="1px"
        borderColor="gray.200"
        maxH="400px"
        overflowY="auto"
      >
        <Table variant="simple" size="sm">
          <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
            <Tr>
              <Th py={3}>Numero da Nota</Th>
              <Th py={3}>Especie</Th>
              <Th py={3}>Nome da Empresa</Th>
              <Th py={3}>ID Empresa</Th>
              <Th py={3}>CNPJ</Th>
            </Tr>
          </Thead>
          <Tbody>
            {notasPaginadas.map(nota => (
              <Tr
                key={`${nota.numero}-${nota.especie}-${nota.empresa?.external_id || 'no-id'}-${nota.empresa?.cnpj || 'no-cnpj'}`}
                _hover={{ bg: 'gray.50' }}
              >
                <Td py={2}>
                  <Text fontWeight="medium" fontSize="sm">
                    {nota.numero}
                  </Text>
                </Td>
                <Td py={2}>
                  <Badge colorScheme={getBadgeColorScheme(nota.especie)} fontSize="xs">
                    {nota.especie}
                  </Badge>
                </Td>
                <Td py={2}>
                  <Text fontSize="sm" noOfLines={1}>
                    {nota.empresa?.nome || '-'}
                  </Text>
                </Td>
                <Td py={2}>
                  <Text fontSize="sm" color="gray.600">
                    {nota.empresa?.external_id || '-'}
                  </Text>
                </Td>
                <Td py={2}>
                  <Text fontSize="sm" fontFamily="mono">
                    {formatCNPJ(nota.empresa?.cnpj || '')}
                  </Text>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>

      {/* Paginacao */}
      <Flex justify="space-between" align="center" mt={4} px={2}>
        <HStack spacing={2}>
          <Text fontSize="sm" color="gray.600">
            Mostrando {notasPaginadas.length} de {totalRegistros} registros
            {tipoFilter && ` (${tipoFilter})`}
          </Text>
          <Select
            size="sm"
            w="80px"
            value={size}
            onChange={e => handleSizeChange(Number(e.target.value))}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Select>
        </HStack>

        <HStack spacing={2}>
          <IconButton
            aria-label="Pagina anterior"
            icon={<CaretLeft size={16} />}
            size="sm"
            variant="outline"
            isDisabled={page <= 1}
            onClick={() => handlePageChange(page - 1)}
          />
          <Text fontSize="sm" color="gray.600">
            Pagina {page} de {totalPages}
          </Text>
          <IconButton
            aria-label="Proxima pagina"
            icon={<CaretRight size={16} />}
            size="sm"
            variant="outline"
            isDisabled={page >= totalPages}
            onClick={() => handlePageChange(page + 1)}
          />
        </HStack>
      </Flex>
    </Box>
  );

  // Render content based on state
  const renderContent = () => {
    if (isFetching) return renderLoading();
    if (notasPaginadas.length === 0) return renderEmpty();
    return renderTable();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent maxH="90vh">
        <ModalHeader borderBottom="1px" borderColor="gray.200" pb={4}>
          <Flex align="center" gap={3}>
            <Icon as={User} color="blue.500" boxSize={6} />
            <Box>
              <Text fontSize="lg" fontWeight="bold">
                {item.name}
              </Text>
              <Text fontSize="sm" color="gray.500">
                Origem: {item.origin}
              </Text>
            </Box>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={4}>
          {/* Cards de Resumo - Clicaveis para filtrar */}
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} mb={6}>
            <Card
              shadow="sm"
              bg={tipoFilter === 'NFE' ? 'green.100' : 'green.50'}
              borderRadius="lg"
              cursor="pointer"
              onClick={() => handleTipoFilter('NFE')}
              border="2px"
              borderColor={tipoFilter === 'NFE' ? 'green.500' : 'transparent'}
              transition="all 0.2s"
              _hover={{ shadow: 'md', borderColor: 'green.300' }}
            >
              <CardBody py={3} px={4}>
                <Flex align="center" gap={3}>
                  <Icon as={FileText} color="green.500" boxSize={6} weight="duotone" />
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      NFE
                    </Text>
                    {showLoadingTotais ? (
                      <Spinner size="sm" color="green.500" />
                    ) : (
                      <Text fontSize="xl" fontWeight="bold" color="green.600">
                        {totalNfe.toLocaleString('pt-BR')}
                      </Text>
                    )}
                  </Box>
                </Flex>
              </CardBody>
            </Card>

            <Card
              shadow="sm"
              bg={tipoFilter === 'NFCE' ? 'orange.100' : 'orange.50'}
              borderRadius="lg"
              cursor="pointer"
              onClick={() => handleTipoFilter('NFCE')}
              border="2px"
              borderColor={tipoFilter === 'NFCE' ? 'orange.500' : 'transparent'}
              transition="all 0.2s"
              _hover={{ shadow: 'md', borderColor: 'orange.300' }}
            >
              <CardBody py={3} px={4}>
                <Flex align="center" gap={3}>
                  <Icon as={ShoppingCart} color="orange.500" boxSize={6} weight="duotone" />
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      NFCE
                    </Text>
                    {showLoadingTotais ? (
                      <Spinner size="sm" color="orange.500" />
                    ) : (
                      <Text fontSize="xl" fontWeight="bold" color="orange.600">
                        {totalNfce.toLocaleString('pt-BR')}
                      </Text>
                    )}
                  </Box>
                </Flex>
              </CardBody>
            </Card>

            <Card
              shadow="sm"
              bg={tipoFilter === 'NFSE' ? 'purple.100' : 'purple.50'}
              borderRadius="lg"
              cursor="pointer"
              onClick={() => handleTipoFilter('NFSE')}
              border="2px"
              borderColor={tipoFilter === 'NFSE' ? 'purple.500' : 'transparent'}
              transition="all 0.2s"
              _hover={{ shadow: 'md', borderColor: 'purple.300' }}
            >
              <CardBody py={3} px={4}>
                <Flex align="center" gap={3}>
                  <Icon as={File} color="purple.500" boxSize={6} weight="duotone" />
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      NFSE
                    </Text>
                    {showLoadingTotais ? (
                      <Spinner size="sm" color="purple.500" />
                    ) : (
                      <Text fontSize="xl" fontWeight="bold" color="purple.600">
                        {totalNfse.toLocaleString('pt-BR')}
                      </Text>
                    )}
                  </Box>
                </Flex>
              </CardBody>
            </Card>

            <Card
              shadow="sm"
              bg={!tipoFilter ? 'gray.200' : 'gray.100'}
              borderRadius="lg"
              cursor="pointer"
              onClick={() => setTipoFilter(null)}
              border="2px"
              borderColor={!tipoFilter ? 'gray.400' : 'transparent'}
              transition="all 0.2s"
              _hover={{ shadow: 'md', borderColor: 'gray.300' }}
            >
              <CardBody py={3} px={4}>
                <Flex align="center" gap={3}>
                  <Box
                    bg="gray.300"
                    borderRadius="full"
                    p={2}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text fontSize="sm" fontWeight="bold" color="gray.600">
                      #
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      Total
                    </Text>
                    {showLoadingTotais ? (
                      <Spinner size="sm" color="gray.500" />
                    ) : (
                      <Text fontSize="xl" fontWeight="bold" color="gray.700">
                        {totalGeral.toLocaleString('pt-BR')}
                      </Text>
                    )}
                  </Box>
                </Flex>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* Indicador de filtro ativo */}
          {tipoFilter && (
            <Box mb={4} p={2} bg="blue.50" borderRadius="md" textAlign="center">
              <Text fontSize="sm" color="blue.700">
                Filtrando por: <strong>{tipoFilter}</strong> - {totalRegistros} registro(s)
                encontrado(s) (clique em Total para ver todos)
              </Text>
            </Box>
          )}

          {/* Filtros */}
          <Box bg="gray.50" p={4} borderRadius="lg" mb={4}>
            <Flex
              direction={{ base: 'column', md: 'row' }}
              gap={4}
              align={{ base: 'stretch', md: 'center' }}
              justify="space-between"
            >
              <HStack spacing={2} flex={1} maxW={{ base: '100%', md: '450px' }}>
                <SearchModeBar
                  modes={SEARCH_MODES}
                  mode={searchMode}
                  onModeChange={m => {
                    setSearchMode(m as 'numero' | 'razao_social' | 'id' | 'cnpj');
                    setPage(1);
                  }}
                  value={searchInput}
                  onValueChange={v => {
                    setSearchInput(v);
                    debouncedSearch(v);
                  }}
                />
              </HStack>

              <MonthNavigator
                currentDate={currentDate}
                onPrevMonth={handlePrevMonth}
                onNextMonth={handleNextMonth}
                onSelectMonth={handleSelectMonth}
              />
            </Flex>
          </Box>

          {/* Tabela de Notas */}
          {renderContent()}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
